import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { z } from 'zod';
import { notifyAPIClient } from '../../utils/toast';
import { Coach } from '../../types/CoachTable.type';

type UpdateCoachDialogProps = {
  open: boolean;
  handleClose: () => void;
  coach: Coach | null;
  handleUpdateCoach: (coach: Coach) => void;
};

type FormErrors = {
  name?: string;
  email?: string;
};

const UpdateCoachDialog = ({
  open,
  handleClose,
  coach,
  handleUpdateCoach,
}: UpdateCoachDialogProps) => {
  const UpdateCoachInput = z.object({
    id: z.number(),
    name: z
      .string()
      .min(1, 'Please enter coach name')
      .refine((val) => val.trim().length > 0, {
        message: 'Coach name cannot be just whitespace',
      }),
    email: z
      .string()
      .min(1, 'Please enter email')
      .email('Invalid email format'),
  });

  type UpdateCoachInputType = z.infer<typeof UpdateCoachInput>;

  const [formErrors, setFormErrors] = React.useState<FormErrors>({});

  const handleConfirm = async (data: UpdateCoachInputType) => {
    try {
      handleUpdateCoach(data as Coach);
      handleClose();
      // Reset form errors
      setFormErrors({});
    } catch (error) {
      notifyAPIClient('An unexpected error occurred while updating the coach.');
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = UpdateCoachInput.parse({
        id: coach?.id,
        name: formData.get('name'),
        email: formData.get('email'),
      });
      setFormErrors({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        setFormErrors(fieldErrors);
      }
    }
  };

  const handleCloseDialog = () => {
    setFormErrors({});
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>Update Coach</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          name="name"
          label="Coach Name"
          type="text"
          fullWidth
          variant="standard"
          defaultValue={coach?.name || ''}
          error={!!formErrors.name}
          helperText={formErrors.name}
        />
        <TextField
          required
          margin="dense"
          id="email"
          name="email"
          label="Email Address"
          type="email"
          fullWidth
          variant="standard"
          defaultValue={coach?.email || ''}
          error={!!formErrors.email}
          helperText={formErrors.email}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button type="submit" variant="contained">
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateCoachDialog;
